// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { Capacitor } from '@capacitor/core';

// DECISION: This module is the ONE source of truth for "are we running inside
// the native Capacitor shell?". The native-shell UI (bottom tab bar, native
// pickers, biometric unlock, Keychain/Keystore storage) is gated on
// isNativePlatform() — NOT on viewport width. A desktop browser resized narrow
// must keep the desktop layout; only the packaged iOS/Android app opts into the
// touch-first shell. Viewport-based responsive CSS remains free to adapt the web
// app for someone visiting the site in a phone browser, but that is a separate
// concern from `isNative`.
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export type NativePlatform = 'ios' | 'android' | 'web';

export function getPlatform(): NativePlatform {
  const p = Capacitor.getPlatform();
  if (p === 'ios' || p === 'android') return p;
  return 'web';
}

export function isIOS(): boolean {
  return getPlatform() === 'ios';
}

export function isAndroid(): boolean {
  return getPlatform() === 'android';
}

// True only in the Capacitor native shell (iOS/Android). Kept as a stable const
// for cheap reuse in module scope. Capacitor-specific features (biometrics,
// haptics, status bar, native pickers) gate on this.
export const IS_NATIVE = isNativePlatform();

// ── Runtime detection (single source of truth) ───────────────────────────────
// DECISION: Hushold will eventually run in three distinct host runtimes, each
// with its own secure-storage mechanism. Rather than scatter `if (Capacitor…)` /
// `if (window.__TAURI__…)` checks across the codebase, ALL runtime branching flows
// through `detectRuntime()` here. Adding a runtime (or a feature that varies by
// runtime) means extending this one function and registering an adapter — never
// editing call sites. See SecureStorageAdapter's platform matrix in
// native/secure-storage.ts for the storage backing each runtime.
export type Runtime = 'capacitor' | 'tauri' | 'web';

interface TauriGlobal {
  __TAURI__?: unknown;
  __TAURI_INTERNALS__?: unknown;
}

// True when running inside a Tauri desktop shell (macOS/Windows/Linux).
export function isTauri(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as TauriGlobal;
  return w.__TAURI__ !== undefined || w.__TAURI_INTERNALS__ !== undefined;
}

// The host runtime, resolved once. Capacitor takes precedence (it also injects a
// WebView global), then Tauri, then plain web.
export function detectRuntime(): Runtime {
  if (isNativePlatform()) return 'capacitor';
  if (isTauri()) return 'tauri';
  return 'web';
}

export const RUNTIME: Runtime = detectRuntime();
